import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { IProvisionListBySinister } from "../../../../shared/services/api/provisoes/ProvisionService";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { useProfile } from "../../../../context/ProfileContext";

// Borderô Agendado: o analista de Sinistro não suspende, cancela nem altera pela tela. Qualquer mudança
// exige falar com o Financeiro, único setor que retira a data/status de Agendado.
export const SCHEDULED_BLOCK_MESSAGE = "Borderô agendado pelo Financeiro — para suspender, cancelar ou alterar, comunique o setor Financeiro";

export type BorderoMenuModalType = "details" | "suspend" | "liftSuspension" | "cancel" | "editBankData";

interface BorderoRowMenuProps {
	anchorEl: null | HTMLElement;
	open: boolean;
	onClose: () => void;
	selectedRow: IProvisionListBySinister | null;
	onOpenModal: (type: BorderoMenuModalType, row: IProvisionListBySinister) => void;
}

// Menu de ações do borderô, dentro da aba Valores do Sinistro (GARV2-1463/1464, item 6) — pedido
// pra existir aqui e não só no módulo Borderô isolado.
export const BorderoRowMenu = ({ anchorEl, open, onClose, selectedRow, onOpenModal }: BorderoRowMenuProps) => {
	const { isSuperAdmin } = useProfile();
	const canSuspend = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.SuspendBordero);
	const canLiftSuspension = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.LiftSuspensionBordero);
	const canCancel = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.CancelBordero);
	const canEditBankData = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.SinisterValuesOptions, PermissionActionsEnum.EditBorderoBankData);
	if (!selectedRow) return null;

	const isSuspended = selectedRow.status === "suspended_sinistro" || selectedRow.status === "suspended_financeiro";
	// "Agendado" (item 16) prevalece sobre o setor de Sinistro — enquanto ativo, não dá pra
	// suspender/cancelar por aqui (só o Financeiro controla, no módulo próprio).
	const isScheduled = selectedRow.status === "scheduled_financeiro";
	// Só o setor de Sinistro pode retirar uma suspensão que o próprio Sinistro fez — suspensão do
	// Financeiro fica travada pro Sinistro (item 14), só visualizável.
	const canLiftThisSuspension = selectedRow.status === "suspended_sinistro";
	const canBeSuspended = !isSuspended && !isScheduled && selectedRow.status !== "canceled" && selectedRow.status !== "paid";
	const canBeCanceled = !isScheduled && selectedRow.status !== "canceled" && selectedRow.status !== "paid";

	return (
		<Menu anchorEl={anchorEl} open={open} onClose={onClose}>
			<MenuItem
				onClick={() => {
					onClose();
					onOpenModal("details", selectedRow);
				}}
			>
				<ListItemIcon>
					<VisibilityIcon fontSize="small" />
				</ListItemIcon>
				<ListItemText primary="Visualizar detalhes" />
			</MenuItem>

			<Divider />

			{isScheduled && (
				<Tooltip title={SCHEDULED_BLOCK_MESSAGE} placement="right">
					<span>
						<MenuItem disabled>
							<ListItemIcon>
								<PauseCircleOutlineIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Suspender" />
						</MenuItem>
					</span>
				</Tooltip>
			)}

			{canBeSuspended && (
				<MenuItem
					onClick={() => {
						onClose();
						onOpenModal("suspend", selectedRow);
					}}
					disabled={!canSuspend}
				>
					<ListItemIcon>
						<PauseCircleOutlineIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Suspender" />
				</MenuItem>
			)}

			{isSuspended && (
				<Tooltip title={canLiftThisSuspension ? "" : "Suspenso pelo Financeiro — só o Financeiro pode retirar"} placement="right">
					<span>
						<MenuItem
							onClick={() => {
								if (!canLiftThisSuspension) return;
								onClose();
								onOpenModal("liftSuspension", selectedRow);
							}}
							disabled={!canLiftSuspension || !canLiftThisSuspension}
						>
							<ListItemIcon>
								<PlayCircleOutlineIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Retirar suspensão" />
						</MenuItem>
					</span>
				</Tooltip>
			)}

			{isScheduled && (
				<Tooltip title={SCHEDULED_BLOCK_MESSAGE} placement="right">
					<span>
						<MenuItem disabled>
							<ListItemIcon>
								<CancelIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary="Cancelar" />
						</MenuItem>
					</span>
				</Tooltip>
			)}

			{canBeCanceled && (
				<MenuItem
					onClick={() => {
						onClose();
						onOpenModal("cancel", selectedRow);
					}}
					disabled={!canCancel}
				>
					<ListItemIcon>
						<CancelIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Cancelar" />
				</MenuItem>
			)}

			<Divider />

			<Tooltip title={isScheduled ? SCHEDULED_BLOCK_MESSAGE : ""} placement="right">
				<span>
					<MenuItem
						onClick={() => {
							if (!canEditBankData || isScheduled) return;
							onClose();
							onOpenModal("editBankData", selectedRow);
						}}
						disabled={!canEditBankData || isScheduled}
					>
						<ListItemIcon>
							<EditIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Editar dados bancários" />
					</MenuItem>
				</span>
			</Tooltip>
		</Menu>
	);
};
